import { Link, useParams } from 'react-router-dom';
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Avatar } from '../components/Avatar';
import { Modal } from '../components/Modal';
import { Ic } from '../components/Icon';
import { EmojiPickerButton } from '../components/EmojiPicker';
import { roomsApi } from '../api/rooms';
import type { HttpError } from '../api/client';
import type { MemberDto, RoomType } from '../api/types';

type Tab = 'settings' | 'requests' | 'members';

export function ManageRoomPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('settings');

  const room = useQuery({ queryKey: ['room', id], queryFn: () => roomsApi.detail(id!), enabled: !!id });
  const requests = useQuery({
    queryKey: ['room', id, 'requests'],
    queryFn: () => roomsApi.joinRequests(id!),
    enabled: !!id,
  });
  const members = useQuery({
    queryKey: ['room', id, 'members'],
    queryFn: () => roomsApi.members(id!),
    enabled: !!id,
  });

  const accept = useMutation({
    mutationFn: (rid: string) => roomsApi.acceptRequest(id!, rid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['room', id, 'requests'] });
      queryClient.invalidateQueries({ queryKey: ['room', id, 'members'] });
    },
  });
  const reject = useMutation({
    mutationFn: (rid: string) => roomsApi.rejectRequest(id!, rid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['room', id, 'requests'] }),
  });

  const [memberToRemove, setMemberToRemove] = useState<MemberDto | null>(null);
  const removeMember = useMutation({
    mutationFn: (userId: string) => roomsApi.removeMember(id!, userId),
    onSuccess: () => {
      setMemberToRemove(null);
      queryClient.invalidateQueries({ queryKey: ['room', id, 'members'] });
    },
  });

  // Settings form state
  const [emoji, setEmoji] = useState('🏆');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RoomType>('PRIVATE');
  const [maxMembers, setMaxMembers] = useState('50');
  const [membersCanInvite, setMembersCanInvite] = useState(true);
  useEffect(() => {
    if (room.data) {
      setEmoji(room.data.emoji ?? '🏆');
      setDescription(room.data.description ?? '');
      setType(room.data.type);
      setMaxMembers(String(room.data.maxMembers));
      setMembersCanInvite(room.data.membersCanInvite);
    }
  }, [room.data]);

  const saveSettings = useMutation({
    mutationFn: () =>
      roomsApi.update(id!, {
        emoji,
        description,
        type,
        maxMembers: Math.max(2, Math.min(500, Number(maxMembers) || 2)),
        membersCanInvite,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['room', id] });
      queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
    },
  });
  const onSaveSettings = (e: FormEvent) => {
    e.preventDefault();
    saveSettings.mutate();
  };

  if (!room.data) return <DesktopShell><TopBar title="Ładowanie…" /></DesktopShell>;

  const tabs: { id: Tab; label: string; badge: number | null }[] = [
    { id: 'settings', label: 'Ustawienia', badge: null },
    { id: 'requests', label: 'Prośby', badge: requests.data?.length ?? 0 },
    { id: 'members', label: 'Członkowie', badge: members.data?.length ?? 0 },
  ];

  return (
    <DesktopShell>
      <TopBar
        title="Zarządzaj pokojem"
        subtitle={`${room.data.emoji ?? '🏆'} ${room.data.name} · widoczne tylko dla właściciela`}
        right={
          <Pill color="accent">
            <Ic.lock size={10} />
            Tylko właściciel
          </Pill>
        }
      />
      <div style={{ flex: 1, padding: '24px 36px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#5b6578', marginBottom: 14 }}>
          <Link to={`/rooms/${id}`} style={{ display: 'inline-flex', alignItems: 'center', color: '#0f1e3d' }}>
            <Ic.chevL size={12} />
            <span style={{ marginLeft: 4 }}>{room.data.name}</span>
          </Link>
          <span>›</span>
          <span style={{ color: '#0f1e3d', fontWeight: 600 }}>Zarządzanie</span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            borderBottom: '1px solid rgba(15,30,61,.08)',
          }}
        >
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '12px 4px',
                  marginRight: 10,
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? '#0f1e3d' : '#5b6578',
                  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: 'inherit',
                }}
              >
                {t.label}
                {t.badge != null && <Pill color={active ? 'accent' : 'gray'}>{t.badge}</Pill>}
              </button>
            );
          })}
        </div>

        {tab === 'settings' && (
          <Card padding={24} style={{ maxWidth: 640 }}>
            <form onSubmit={onSaveSettings}>
              <FieldLabel>NAZWA POKOJU</FieldLabel>
              <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', marginBottom: 16 }}>
                <EmojiPickerButton value={emoji} onChange={setEmoji} size={40} />
                <input
                  value={room.data.name}
                  readOnly
                  disabled
                  style={{
                    ...inputStyle,
                    flex: 1,
                    background: '#f4f3ee',
                    color: '#5b6578',
                    cursor: 'not-allowed',
                  }}
                />
              </div>

              <FieldLabel>OPIS</FieldLabel>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Krótki opis pokoju (opcjonalny)"
                rows={3}
                style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical', marginBottom: 16 }}
              />

              <FieldLabel>TYP POKOJU</FieldLabel>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {(['PRIVATE', 'PUBLIC'] as RoomType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    style={{
                      flex: 1,
                      height: 40,
                      borderRadius: 9,
                      border: '1px solid',
                      borderColor: type === t ? '#0f1e3d' : 'rgba(15,30,61,.14)',
                      background: type === t ? '#0f1e3d' : '#fff',
                      color: type === t ? '#fff' : '#0f1e3d',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {t === 'PRIVATE' ? 'Prywatny' : 'Publiczny'}
                  </button>
                ))}
              </div>

              <FieldLabel>MAKSYMALNA LICZBA CZŁONKÓW</FieldLabel>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={maxMembers}
                onChange={(e) => setMaxMembers(e.target.value.replace(/[^0-9]/g, ''))}
                style={{ ...inputStyle, width: 120, marginBottom: 16 }}
              />

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 18,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={membersCanInvite}
                  onChange={(e) => setMembersCanInvite(e.target.checked)}
                />
                Członkowie mogą zapraszać innych
              </label>

              {saveSettings.isError && (
                <div
                  style={{
                    padding: 12,
                    background: 'rgba(208,70,60,.08)',
                    color: '#8a2a22',
                    borderRadius: 10,
                    fontSize: 13,
                    marginBottom: 14,
                  }}
                >
                  {(saveSettings.error as HttpError).body?.message ?? 'Nie udało się zapisać ustawień.'}
                </div>
              )}
              {saveSettings.isSuccess && (
                <div
                  style={{
                    padding: 12,
                    background: 'rgba(31,157,85,.1)',
                    color: '#0f6b34',
                    borderRadius: 10,
                    fontSize: 13,
                    marginBottom: 14,
                  }}
                >
                  Ustawienia zapisane.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn type="submit" variant="accent" icon={<Ic.check size={14} />} disabled={saveSettings.isPending}>
                  {saveSettings.isPending ? 'Zapisywanie…' : 'Zapisz ustawienia'}
                </Btn>
              </div>
            </form>
          </Card>
        )}

        {tab === 'requests' && (
          <Card padding={0} style={{ maxWidth: 640 }}>
            {requests.data && requests.data.length > 0 ? (
              requests.data.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    padding: '16px 22px',
                    borderBottom: i < (requests.data?.length ?? 0) - 1 ? '1px solid rgba(15,30,61,.06)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                  }}
                >
                  <Avatar nick={r.nick ?? '?'} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 14.5 }}>{r.nick}</div>
                      <Pill color="gray">{r.email}</Pill>
                    </div>
                    <div style={{ fontSize: 12.5, color: '#5b6578', marginTop: 4 }}>
                      Prośba wysłana {new Date(r.createdAt).toLocaleString('pl-PL')}
                    </div>
                  </div>
                  <Btn variant="ghost" size="sm" onClick={() => reject.mutate(r.id)} disabled={reject.isPending}>
                    Odrzuć
                  </Btn>
                  <Btn
                    variant="success"
                    size="sm"
                    icon={<Ic.check size={13} />}
                    onClick={() => accept.mutate(r.id)}
                    disabled={accept.isPending}
                  >
                    Zaakceptuj
                  </Btn>
                </div>
              ))
            ) : (
              <div style={{ padding: 24, color: '#5b6578', textAlign: 'center' }}>
                Brak oczekujących próśb o dołączenie.
              </div>
            )}
          </Card>
        )}

        {tab === 'members' && (
          <Card padding={0} style={{ maxWidth: 640 }}>
            {(members.data ?? []).map((m, i) => (
              <div
                key={m.userId}
                style={{
                  padding: '14px 22px',
                  borderBottom: i < (members.data?.length ?? 0) - 1 ? '1px solid rgba(15,30,61,.06)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                }}
              >
                <Avatar nick={m.nick} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {m.nick}
                    {m.role === 'OWNER' && <Pill color="accent" style={{ marginLeft: 8 }}>OWNER</Pill>}
                  </div>
                  <div style={{ fontSize: 12, color: '#5b6578', marginTop: 2 }}>
                    {m.email} · dołączył {new Date(m.joinedAt).toLocaleDateString('pl-PL')}
                  </div>
                </div>
                {m.role !== 'OWNER' && (
                  <Btn
                    variant="danger"
                    size="sm"
                    icon={<Ic.trash size={13} />}
                    onClick={() => setMemberToRemove(m)}
                  >
                    Usuń
                  </Btn>
                )}
              </div>
            ))}
          </Card>
        )}

      </div>

      <Modal
        open={!!memberToRemove}
        onClose={() => !removeMember.isPending && setMemberToRemove(null)}
        title="Usunąć członka z pokoju?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setMemberToRemove(null)} disabled={removeMember.isPending}>
              Anuluj
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.trash size={14} />}
              onClick={() => memberToRemove && removeMember.mutate(memberToRemove.userId)}
              disabled={removeMember.isPending}
            >
              {removeMember.isPending ? 'Usuwanie…' : 'Usuń członka'}
            </Btn>
          </>
        }
      >
        {memberToRemove && (
          <>
            <p style={{ margin: '0 0 8px' }}>
              Użytkownik <strong>{memberToRemove.nick}</strong> przestanie być członkiem pokoju i nie będzie się
              pojawiał w leaderboardzie.
            </p>
            <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
              Otrzyma powiadomienie w aplikacji o usunięciu z pokoju. Może zostać zaproszony ponownie.
            </p>
            {removeMember.isError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: 'rgba(208,70,60,.08)',
                  color: '#8a2a22',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                {(removeMember.error as HttpError).body?.message ?? 'Nie udało się usunąć członka.'}
              </div>
            )}
          </>
        )}
      </Modal>
    </DesktopShell>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: '#5b6578',
        fontWeight: 600,
        letterSpacing: '.06em',
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  borderRadius: 9,
  border: '1px solid rgba(15,30,61,.14)',
  padding: '0 12px',
  fontSize: 14,
  background: '#fff',
  outline: 'none',
  fontFamily: 'inherit',
};
